// Chunked / resumable upload client for /api/media/uploads
//
// The Emergent Object Storage backing our media does not support presigned
// URLs, so every byte still flows through the FastAPI backend. To make that
// reliable on flaky mobile connections we:
//   * split the file into fixed-size chunks (default 5 MiB)
//   * hash the file (SHA-256) so the server can dedupe/re-anchor a session
//   * report REAL byte-level progress via `onProgress`
//   * expose `cancel()` for the caller (used by an in-flight ref in the UI)
//   * retry each chunk on transient (5xx / network) failures with backoff
//   * prevent double-tap uploads via the caller-owned in-flight ref
//
// Public API:
//   const controller = uploadFileChunked(file, opts) → { promise, cancel }
//
// Options:
//   name              – display name (default: file basename)
//   mediaType         – 'video' | 'image'
//   durationSeconds   – client-measured duration for videos
//   chunkSize         – override server-advertised chunk size (bytes)
//   maxRetries        – per-chunk retry cap (default 3)
//   onProgress        – (pct: 0-100, info: {loaded, total, phase}) => void
//                       phases: 'hashing' | 'uploading' | 'finalizing'

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'futureself_auth_token';

// Pull the current Bearer token (if any). Mirrors the resilient-auth
// strategy in authFetch.js: cookie is primary, Bearer is the fallback
// used when ITP / cross-origin cookie policies drop the session cookie.
// NB: `window.fetch` is monkey-patched by `installAuthFetch()` so plain
// `fetch` calls get the header for free, but `XMLHttpRequest` is NOT
// patched — chunk uploads must add it themselves.
const safeGetToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

// Extension → MIME fallback. iOS Safari (and some Android pickers) often
// hand us a `file.type` that is empty or 'application/octet-stream' when the
// user picks a video straight from the Photos library. In that case we sniff
// the filename extension so the backend whitelist accepts the upload.
const VIDEO_EXT_MIME = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  qt: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  hevc: 'video/mp4',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
};
const IMAGE_EXT_MIME = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};

export function resolveContentType(file, mediaType) {
  const raw = (file && file.type ? String(file.type).toLowerCase() : '').trim();
  if (raw && raw !== 'application/octet-stream') return raw;
  const name = (file && file.name) || '';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  if (!ext) return raw || 'application/octet-stream';
  if (mediaType === 'video' && VIDEO_EXT_MIME[ext]) return VIDEO_EXT_MIME[ext];
  if (mediaType === 'image' && IMAGE_EXT_MIME[ext]) return IMAGE_EXT_MIME[ext];
  return raw || 'application/octet-stream';
}

export function isSupportedVideoFile(file) {
  if (!file) return false;
  const t = (file.type || '').toLowerCase();
  if (t.startsWith('video/')) return true;
  const name = file.name || '';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return Boolean(VIDEO_EXT_MIME[ext]);
}

// Compute SHA-256 as a lowercase hex string using WebCrypto.
async function sha256Hex(file, onProgress) {
  // We stream-hash to avoid loading a 100MB file into memory as an ArrayBuffer.
  // WebCrypto has no incremental API, so we buffer the file in one shot.
  // For our 100MB cap this is acceptable on modern devices, but we report
  // progress so the UI never freezes silently.
  if (typeof onProgress === 'function') onProgress(0, { phase: 'hashing' });
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i += 1) {
      hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
  } catch (_e) {
    // iOS WebKit can fail to buffer very large files in memory. The hash is
    // only used for dedupe/resume, so degrade gracefully instead of dying.
    return null;
  }
}

function toContentTypeHeader(chunkSize) {
  return { 'Content-Type': 'application/octet-stream', 'Content-Length': String(chunkSize) };
}

async function withCredentialsFetch(url, opts = {}) {
  return fetch(url, { credentials: 'include', ...opts });
}

async function initSession({ file, name, mediaType, sha256, durationSeconds, replaceBackgroundId, signal }) {
  const body = {
    filename: file.name || 'upload.bin',
    content_type: resolveContentType(file, mediaType),
    total_size: file.size,
    media_type: mediaType,
    sha256,
    name,
    duration_seconds: durationSeconds || null,
    replace_background_id: replaceBackgroundId || null,
  };
  const resp = await withCredentialsFetch(`${API_URL}/api/media/uploads/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(json.detail || json.message || `Init failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return json.data || json;
}

function sendChunkXHR({
  uploadId,
  index,
  chunk,
  onLoaded,
  signal,
}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/media/uploads/${uploadId}/chunk?index=${index}`, true);
    xhr.withCredentials = true;
    xhr.timeout = 5 * 60 * 1000; // 5 min per chunk

    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && typeof onLoaded === 'function') {
        onLoaded(evt.loaded);
      }
    };
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText || '{}'); } catch (_) { data = {}; }
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        const err = new Error(data.detail || data.message || `Chunk ${index} failed (${xhr.status})`);
        err.status = xhr.status;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error('Network error during chunk upload'));
    xhr.ontimeout = () => reject(new Error('Chunk upload timed out'));
    xhr.onabort = () => {
      const err = new Error('Upload cancelled');
      err.aborted = true;
      reject(err);
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    // Attach the resilient-auth Bearer token so chunk uploads continue to
    // work when iOS WKWebView ITP has dropped the session cookie. The
    // backend accepts either the `session_token` cookie OR
    // `Authorization: Bearer <jwt>` (see backend/core/auth.py).
    const token = safeGetToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(chunk);
  });
}

async function completeSession(uploadId, signal) {
  const resp = await withCredentialsFetch(`${API_URL}/api/media/uploads/${uploadId}/complete`, {
    method: 'POST',
    signal,
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(json.detail || json.message || `Finalize failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return json.data || json;
}

async function cancelSession(uploadId) {
  if (!uploadId) return;
  try {
    await withCredentialsFetch(`${API_URL}/api/media/uploads/${uploadId}`, {
      method: 'DELETE',
    });
  } catch (_e) {
    // Best-effort; server-side cleanup will sweep the temp file within 2h.
  }
}

// Public entry point.
export function uploadFileChunked(file, opts = {}) {
  const {
    name,
    mediaType,
    durationSeconds,
    chunkSize,
    maxRetries = 3,
    onProgress,
    replaceBackgroundId,
    // How many times to fully restart the upload from chunk 0 if the server
    // loses its staging file (backend pod restart mid-upload). Each restart
    // re-inits a fresh session (the previous one is marked failed server-side).
    maxStagingRestarts = 2,
  } = opts;

  const abort = new AbortController();
  let started = false;
  let uploadId = null;

  const promise = (async () => {
    started = true;
    // 1. hash for idempotency (and progress feedback)
    const sha = await sha256Hex(file, onProgress);
    if (abort.signal.aborted) throw makeAbortError();

    let stagingRestarts = 0;

    // Everything from init → chunks → complete lives in this restart loop so
    // that a mid-upload "staging lost" (HTTP 410) can transparently rewind
    // to chunk 0 without the user having to press retry.
    while (true) {
      // 2. init session
      const init = await initSession({
        file,
        name,
        mediaType,
        sha256: sha,
        durationSeconds,
        replaceBackgroundId,
        signal: abort.signal,
      });

      if (init.duplicate) {
        // Server detected an existing background with the same SHA-256.
        // Skip the upload entirely and return the record straight away.
        if (typeof onProgress === 'function') onProgress(100, { phase: 'finalizing' });
        return init.background;
      }

      uploadId = init.upload_id;
      const serverChunkSize = init.chunk_size || 5 * 1024 * 1024;
      const effectiveChunkSize = chunkSize || serverChunkSize;

      const total = file.size;
      const startBytes = init.uploaded_bytes || 0;
      const startIndex = init.chunks_received || 0;

      let uploaded = startBytes;
      if (typeof onProgress === 'function') {
        onProgress((uploaded / total) * 100, { phase: 'uploading', loaded: uploaded, total });
      }

      // 3. send chunks sequentially, in order
      let index = startIndex;
      let cursor = startBytes;
      let stagingLost = false;
      while (cursor < total) {
        if (abort.signal.aborted) throw makeAbortError();
        const end = Math.min(cursor + effectiveChunkSize, total);
        const slice = file.slice(cursor, end);
        const sliceBytes = end - cursor;

        let attempt = 0;
        let lastErr = null;
        const baselineUploaded = uploaded;

        while (attempt <= maxRetries) {
          try {
            await sendChunkXHR({
              uploadId,
              index,
              chunk: slice,
              onLoaded: (loaded) => {
                const clamped = Math.min(loaded, sliceBytes);
                const totalLoaded = baselineUploaded + clamped;
                if (typeof onProgress === 'function') {
                  onProgress(Math.min(99, (totalLoaded / total) * 100), {
                    phase: 'uploading',
                    loaded: totalLoaded,
                    total,
                  });
                }
              },
              signal: abort.signal,
            });
            uploaded += sliceBytes;
            if (typeof onProgress === 'function') {
              onProgress(Math.min(99, (uploaded / total) * 100), {
                phase: 'uploading',
                loaded: uploaded,
                total,
              });
            }
            break; // success
          } catch (err) {
            lastErr = err;
            if (err.aborted || abort.signal.aborted) throw makeAbortError();
            // 410 = server lost staging file (pod restart). Rewind to chunk 0.
            if (err.status === 410) {
              stagingLost = true;
              break;
            }
            // Do not retry other validation / auth errors
            if (err.status && err.status >= 400 && err.status < 500 && err.status !== 429) {
              throw err;
            }
            attempt += 1;
            if (attempt > maxRetries) break;
            await sleep(600 * attempt);
          }
        }
        if (stagingLost) break;
        if (attempt > maxRetries && lastErr) throw lastErr;
        index += 1;
        cursor = end;
      }

      if (stagingLost) {
        if (stagingRestarts >= maxStagingRestarts) {
          throw new Error(
            'Upload restarted too many times. Please try again in a moment.'
          );
        }
        stagingRestarts += 1;
        if (typeof onProgress === 'function') {
          onProgress(0, { phase: 'uploading', loaded: 0, total: file.size });
        }
        // Loop → re-init a fresh session and restart from chunk 0.
        uploadId = null;
        continue;
      }

      // 4. finalize → server pushes assembled file to object storage
      if (typeof onProgress === 'function') onProgress(100, { phase: 'finalizing', loaded: file.size, total: file.size });
      try {
        const record = await completeSession(uploadId, abort.signal);
        return record;
      } catch (err) {
        if (err.status === 410 && stagingRestarts < maxStagingRestarts) {
          // Staging lost between last chunk and complete — rewind and try again.
          stagingRestarts += 1;
          if (typeof onProgress === 'function') {
            onProgress(0, { phase: 'uploading', loaded: 0, total: file.size });
          }
          uploadId = null;
          continue;
        }
        throw err;
      }
    }
  })().catch(async (err) => {
    if (err && err.aborted && uploadId) {
      await cancelSession(uploadId);
    }
    throw err;
  });

  return {
    promise,
    cancel: () => {
      if (!started) return;
      abort.abort();
      // Best-effort server-side release (fire & forget)
      if (uploadId) cancelSession(uploadId);
    },
  };
}

function makeAbortError() {
  const err = new Error('Upload cancelled');
  err.aborted = true;
  return err;
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// -------------------- Client-side helpers --------------------

// Extract a thumbnail (jpeg blob) at ~1s into a video, on-device.
// Falls back to null if the browser refuses to decode the frame.
export async function extractVideoThumbnail(file) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    await new Promise((resolve, reject) => {
      video.onloadedmetadata = resolve;
      video.onerror = () => reject(new Error('metadata error'));
    });
    // Seek to 1s (or 10% if very short)
    const seekTo = Math.min(Math.max(1, video.duration * 0.1), Math.max(0.1, video.duration - 0.1));
    video.currentTime = seekTo;
    await new Promise((resolve, reject) => {
      video.onseeked = resolve;
      video.onerror = () => reject(new Error('seek error'));
    });
    const canvas = document.createElement('canvas');
    // Cap thumbnail at 720p width to stay small
    const targetWidth = Math.min(720, video.videoWidth || 720);
    const ratio = (video.videoHeight || 720) / (video.videoWidth || 720);
    canvas.width = targetWidth;
    canvas.height = Math.round(targetWidth * ratio);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    return { blob, dataUrl: canvas.toDataURL('image/jpeg', 0.82), duration: video.duration };
  } catch (_e) {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Read the intrinsic duration of a video without decoding a frame.
// Hard timeout so a stubborn iOS WebKit decode can NEVER hang the upload flow.
export async function readVideoDuration(file, timeoutMs = 8000) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    await new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('metadata timeout')), timeoutMs);
      video.onloadedmetadata = () => { clearTimeout(timer); resolve(); };
      video.onerror = () => { clearTimeout(timer); reject(new Error('metadata error')); };
    });
    return Number.isFinite(video.duration) ? video.duration : null;
  } catch (_e) {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
